// Pure TS Layer: React 非依存
// AWS IoT Core MQTT over WSS の URL を SigV4 で署名する。
//
// 注意: AWS IoT MQTT WSS は X-Amz-Security-Token を「署名計算後に URL に追加する」
// パターンを要求する (aws-amplify / aws-iot-device-sdk-js v1 と同じ)。
// @aws-sdk/signature-v4 の presign は token を canonical request に含めるため
// IoT では 403 になる。本ファイルでは Web Crypto API でカスタム実装する。
//
// 参考: https://docs.aws.amazon.com/general/latest/gr/sigv4_signing.html
//      https://docs.aws.amazon.com/iot/latest/developerguide/protocols.html

import type { AwsCredentials } from '@/hooks/auth/cognitoCredentials';

export type SignWebSocketUrlInput = {
  readonly region: string;
  readonly iotEndpoint: string; // 例: xxxxxxxx-ats.iot.ap-northeast-1.amazonaws.com
  readonly credentials: AwsCredentials;
};

const SERVICE = 'iotdevicegateway';
const ALGORITHM = 'AWS4-HMAC-SHA256';

// ----------------------------------------------------------------------
// Helpers: ArrayBuffer / Hex / TextEncoder
// ----------------------------------------------------------------------

function toHex(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf);
  let out = '';
  for (let i = 0; i < bytes.length; i++) {
    const b = bytes[i] as number;
    out += b.toString(16).padStart(2, '0');
  }
  return out;
}

const utf8 = new TextEncoder();

async function sha256Hex(input: string): Promise<string> {
  const hash = await crypto.subtle.digest('SHA-256', utf8.encode(input));
  return toHex(hash);
}

async function hmacSha256(
  key: ArrayBuffer | Uint8Array,
  data: string,
): Promise<ArrayBuffer> {
  const cryptoKey = await crypto.subtle.importKey(
    'raw',
    key as BufferSource,
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign'],
  );
  return crypto.subtle.sign('HMAC', cryptoKey, utf8.encode(data));
}

// ----------------------------------------------------------------------
// AWS SigV4 形式の日時文字列
// ----------------------------------------------------------------------
function formatAmzDate(d: Date): { date: string; datetime: string } {
  // YYYYMMDD
  const date = `${d.getUTCFullYear()}${String(d.getUTCMonth() + 1).padStart(2, '0')}${String(d.getUTCDate()).padStart(2, '0')}`;
  // YYYYMMDDTHHMMSSZ
  const datetime = `${date}T${String(d.getUTCHours()).padStart(2, '0')}${String(d.getUTCMinutes()).padStart(2, '0')}${String(d.getUTCSeconds()).padStart(2, '0')}Z`;
  return { date, datetime };
}

// ----------------------------------------------------------------------
// AWS SigV4 URI encoding (RFC 3986 strict)
//   encodeURIComponent と異なり、'!' '~' '*' "'" "(" ")" もエンコードする必要あり
// ----------------------------------------------------------------------
function awsUriEncode(value: string): string {
  return encodeURIComponent(value).replace(
    /[!'()*]/g,
    (c) => `%${c.charCodeAt(0).toString(16).toUpperCase()}`,
  );
}

// ----------------------------------------------------------------------
// SigV4 by Web Crypto API
// ----------------------------------------------------------------------

/**
 * IoT Core MQTT WSS のための SigV4 署名済み URL を生成する。
 *
 * フロー:
 *   1. canonical query string (token なし、アルファベット順)
 *   2. canonical request を組み立て
 *   3. string to sign
 *   4. 署名キーを派生
 *   5. 署名を計算
 *   6. URL を組み立て (token は最後に追加)
 */
export async function signWebSocketUrl(input: SignWebSocketUrlInput): Promise<string> {
  const { region, iotEndpoint, credentials } = input;
  const now = new Date();
  const { date, datetime } = formatAmzDate(now);
  const credentialScope = `${date}/${region}/${SERVICE}/aws4_request`;
  const credentialValue = `${credentials.accessKeyId}/${credentialScope}`;

  // 1. canonical query string (sorted alphabetically by parameter name)
  //    X-Amz-Security-Token は含めない (IoT は最後に追加するパターンを要求)
  const canonicalQuery = [
    `X-Amz-Algorithm=${awsUriEncode(ALGORITHM)}`,
    `X-Amz-Credential=${awsUriEncode(credentialValue)}`,
    `X-Amz-Date=${awsUriEncode(datetime)}`,
    `X-Amz-Expires=${awsUriEncode('300')}`,
    `X-Amz-SignedHeaders=${awsUriEncode('host')}`,
  ].join('&');

  // 2. canonical request
  const canonicalHeaders = `host:${iotEndpoint}\n`;
  const signedHeaders = 'host';
  const payloadHash = await sha256Hex(''); // empty body
  const canonicalRequest = [
    'GET',
    '/mqtt',
    canonicalQuery,
    canonicalHeaders,
    signedHeaders,
    payloadHash,
  ].join('\n');

  // 3. string to sign
  const hashedCanonicalRequest = await sha256Hex(canonicalRequest);
  const stringToSign = [ALGORITHM, datetime, credentialScope, hashedCanonicalRequest].join('\n');

  // 4. signature key
  const kDate = await hmacSha256(utf8.encode(`AWS4${credentials.secretAccessKey}`), date);
  const kRegion = await hmacSha256(kDate, region);
  const kService = await hmacSha256(kRegion, SERVICE);
  const kSigning = await hmacSha256(kService, 'aws4_request');

  // 5. signature
  const signatureBuf = await hmacSha256(kSigning, stringToSign);
  const signature = toHex(signatureBuf);

  // 6. URL 組み立て (token は最後に追加)
  let url = `wss://${iotEndpoint}/mqtt?${canonicalQuery}&X-Amz-Signature=${signature}`;
  url += `&X-Amz-Security-Token=${awsUriEncode(credentials.sessionToken)}`;

  return url;
}
